'use client';

import Link from 'next/link';
import Header from '@/components/common/Header';
import { TestType } from '@/types';
import { useState } from 'react';
import TestConfigModal from '@/components/settings/TestConfigModal';
import MockTestConfigModal from '@/components/settings/MockTestConfigModal';
import { useRouter } from 'next/navigation';
import AdPlacement from '@/components/Ads/AdPlacement';

const testTypes: { type: TestType; label: string; description: string }[] = [
    {
        type: 'reasoning',
        label: 'Reasoning',
        description: 'Logic-based questions with comparative statements'
    },
    {
        type: 'perceptual',
        label: 'Perceptual Speed',
        description: 'Letter pair matching exercises'
    },
    {
        type: 'numerical',
        label: 'Number Speed & Accuracy',
        description: 'Quick mathematical comparisons'
    },
    {
        type: 'verbal',
        label: 'Word Meaning',
        description: 'Identify relationships between words'
    },
    {
        type: 'spatial',
        label: 'Spatial Visualization',
        description: 'Symbol rotation and mirror exercises'
    }
];

export default function Home() {
    const [configType, setConfigType] = useState<TestType | null>(null);
    const [isMockOpen, setIsMockOpen] = useState(false);
    const router = useRouter();

    return (
        <>
            <Header />
            <main className="container mx-auto px-4 py-8">
                <div className="flex flex-col lg:flex-row gap-8">
                    <div className="flex-grow">
                        <h1 className="text-4xl font-bold mb-4">Free Thomas GIA Practice Tests</h1>
                        <p className="text-lg text-gray-600 mb-8">
                            Comprehensive free test preparation suite for the Thomas General Intelligence Assessment (GIA). 
                            Practice with our free test pack designed to help you succeed in general aptitude tests.
                        </p>
                        {/* Informative box about data privacy */}
                        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-6" role="alert">
                            <strong className="font-bold">Note: </strong>
                            <p className="block sm:inline">This site does not collect any personal user information. All test data is stored locally in your browser. Enjoy! 🎉</p>
                            {/* <p className="mt-2 flex items-center">If you think this site is helpful, please consider 
                                <a href="https://www.buymeacoffee.com/dandrake" className="inline-block ml-2">
                                    <img src="https://img.buymeacoffee.com/button-api/?text=Buy me a coffee&emoji=&slug=dandrake&button_colour=FFDD00&font_colour=000000&font_family=Cookie&outline_colour=000000&coffee_colour=ffffff" />
                                </a>
                            </p> */}
                            <button
                                type="button"
                                className="absolute bottom-1 right-1 px-2 py-1 text-green-700 hover:text-green-900 focus:outline-none"
                                onClick={() => {
                                    const element = document.querySelector('.bg-green-100') as HTMLElement;
                                    if (element) {
                                        element.style.display = 'none';
                                    }
                                }}
                            >
                                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
                            </button>
                        </div>

                        {/* <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative mb-6" role="alert">
                            <strong className="font-bold">Check out my new macOS app -&gt; </strong>
                            <a href="https://nichepark.com/pastemd" className="block sm:inline font-semibold hover:underline animate-pulse">PasteMD</a>
                            <p className="block sm:inline"> Copy and paste any text on your Desktop without creating a file first</p>
                        </div> */}

                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-2xl font-semibold">Available Practice Tests</h2>
                            <button
                                onClick={() => setIsMockOpen(true)}
                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                                title="Full-length mock session across all components"
                            >
                                Start Mock Test
                            </button>
                        </div>
                        <div className="grid md:grid-cols-2 gap-6">
                            {testTypes.map(({ type, label, description }) => (
                                <article
                                    key={type}
                                    className="group relative"
                                >
                                    <Link
                                        href={`/practice/${type}/intro`}
                                        className="block p-6 border rounded-lg hover:border-blue-500 transition-colors"
                                    >
                                        <h3 className="text-xl font-semibold mb-2">{label}</h3>
                                        <p className="text-gray-600">{description}</p>
                                        <p className="mt-4 text-blue-600 group-hover:text-blue-700">
                                            Start free practice test →
                                        </p>
                                    </Link>
                                    <button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setConfigType(type);
                                        }}
                                        className="absolute top-2 right-2 p-2 rounded-full bg-white border border-slate-200 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity hover:border-slate-300"
                                        title="Configure test"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                    </button>
                                </article>
                            ))}
                        </div>

                        {/* Learn Section */}
                        <section className="mt-12">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-2xl font-semibold">Learn & Improve</h2>
                                <Link 
                                    href="/learn" 
                                    className="text-blue-600 hover:text-blue-700 font-medium"
                                >
                                    View all guides →
                                </Link>
                            </div>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {/* What is Thomas GIA */}
                                <Link href="/learn/what-is-thomas-gia" className="group">
                                    <div className="bg-white border rounded-lg p-6 hover:border-blue-500 hover:shadow-md transition-all">
                                        <div className="flex items-start space-x-4">
                                            <div className="flex-shrink-0">
                                                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                                                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                </div>
                                            </div>
                                            <div className="flex-grow">
                                                <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">What is the Thomas GIA?</h3>
                                                <p className="text-sm text-gray-600 mt-2">Comprehensive guide to understanding the assessment format, components, and scoring system.</p>
                                            </div>
                                        </div>
                                    </div>
                                </Link>

                                {/* Practice App Guide */}
                                <Link href="/learn/guide-to-this-practice-app" className="group">
                                    <div className="bg-white border rounded-lg p-6 hover:border-blue-500 hover:shadow-md transition-all">
                                        <div className="flex items-start space-x-4">
                                            <div className="flex-shrink-0">
                                                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 transition-colors">
                                                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                                    </svg>
                                                </div>
                                            </div>
                                            <div className="flex-grow">
                                                <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">How to Use This Practice Suite</h3>
                                                <p className="text-sm text-gray-600 mt-2">Complete guide to maximizing your preparation with our platform features and study strategies.</p>
                                            </div>
                                        </div>
                                    </div>
                                </Link>

                                {/* Reasoning Tips */}
                                <Link href="/learn/reasoning-tips-examples" className="group">
                                    <div className="bg-white border rounded-lg p-6 hover:border-blue-500 hover:shadow-md transition-all">
                                        <div className="flex items-start space-x-4">
                                            <div className="flex-shrink-0">
                                                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                                                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                                    </svg>
                                                </div>
                                            </div>
                                            <div className="flex-grow">
                                                <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">Reasoning Tips & Examples</h3>
                                                <p className="text-sm text-gray-600 mt-2">Master logical deduction with strategies for handling comparisons, negations, and complex patterns.</p>
                                            </div>
                                        </div>
                                    </div>
                                </Link>

                                {/* Perceptual Speed */}
                                <Link href="/learn/perceptual-speed-guide" className="group">
                                    <div className="bg-white border rounded-lg p-6 hover:border-blue-500 hover:shadow-md transition-all">
                                        <div className="flex items-start space-x-4">
                                            <div className="flex-shrink-0">
                                                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center group-hover:bg-indigo-200 transition-colors">
                                                    <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                                    </svg>
                                                </div>
                                            </div>
                                            <div className="flex-grow">
                                                <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">Perceptual Speed Guide</h3>
                                                <p className="text-sm text-gray-600 mt-2">Develop rapid visual processing skills with proven scanning techniques and accuracy methods.</p>
                                            </div>
                                        </div>
                                    </div>
                                </Link>

                                {/* Number Speed & Accuracy */}
                                <Link href="/learn/number-speed-accuracy-tips" className="group">
                                    <div className="bg-white border rounded-lg p-6 hover:border-blue-500 hover:shadow-md transition-all">
                                        <div className="flex items-start space-x-4">
                                            <div className="flex-shrink-0">
                                                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center group-hover:bg-teal-200 transition-colors">
                                                    <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                                                    </svg>
                                                </div>
                                            </div>
                                            <div className="flex-grow">
                                                <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">Number Speed & Accuracy</h3>
                                                <p className="text-sm text-gray-600 mt-2">Master numerical reasoning with efficient calculation strategies and mental math optimization.</p>
                                            </div>
                                        </div>
                                    </div>
                                </Link>

                                {/* Word Meaning */}
                                <Link href="/learn/word-meaning-strategies" className="group">
                                    <div className="bg-white border rounded-lg p-6 hover:border-blue-500 hover:shadow-md transition-all">
                                        <div className="flex items-start space-x-4">
                                            <div className="flex-shrink-0">
                                                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                                                    <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                                                    </svg>
                                                </div>
                                            </div>
                                            <div className="flex-grow">
                                                <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">Word Meaning Strategies</h3>
                                                <p className="text-sm text-gray-600 mt-2">Excel at semantic categorization with vocabulary building and rapid elimination techniques.</p>
                                            </div>
                                        </div>
                                    </div>
                                </Link>

                                {/* Spatial Visualization */}
                                <Link href="/learn/spatial-visualization-guide" className="group">
                                    <div className="bg-white border rounded-lg p-6 hover:border-blue-500 hover:shadow-md transition-all">
                                        <div className="flex items-start space-x-4">
                                            <div className="flex-shrink-0">
                                                <div className="w-12 h-12 bg-rose-100 rounded-lg flex items-center justify-center group-hover:bg-rose-200 transition-colors">
                                                    <svg className="w-6 h-6 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                    </svg>
                                                </div>
                                            </div>
                                            <div className="flex-grow">
                                                <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">Spatial Visualization Guide</h3>
                                                <p className="text-sm text-gray-600 mt-2">Master 3D mental manipulation with rotation and mirroring techniques for symbol comparison.</p>
                                            </div>
                                        </div>
                                    </div>
                                </Link>

                                {/* Time Management */}
                                <Link href="/learn/time-management-and-accuracy" className="group">
                                    <div className="bg-white border rounded-lg p-6 hover:border-blue-500 hover:shadow-md transition-all">
                                        <div className="flex items-start space-x-4">
                                            <div className="flex-shrink-0">
                                                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                                                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                </div>
                                            </div>
                                            <div className="flex-grow">
                                                <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">Time Management & Accuracy</h3>
                                                <p className="text-sm text-gray-600 mt-2">Balance speed and accuracy with proven pacing strategies and performance optimization.</p>
                                            </div>
                                        </div>
                                    </div>
                                </Link>

                                {/* Interpreting Results */}
                                <Link href="/learn/interpreting-your-results" className="group">
                                    <div className="bg-white border rounded-lg p-6 hover:border-blue-500 hover:shadow-md transition-all">
                                        <div className="flex items-start space-x-4">
                                            <div className="flex-shrink-0">
                                                <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center group-hover:bg-cyan-200 transition-colors">
                                                    <svg className="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                                    </svg>
                                                </div>
                                            </div>
                                            <div className="flex-grow">
                                                <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">Interpreting Your Results</h3>
                                                <p className="text-sm text-gray-600 mt-2">Understand performance metrics, analyze trends, and develop targeted improvement strategies.</p>
                                            </div>
                                        </div>
                                    </div>
                                </Link>

                                {/* How to Improve Over Time */}
                                <Link href="/learn/how-to-improve-over-time" className="group">
                                    <div className="bg-white border rounded-lg p-6 hover:border-blue-500 hover:shadow-md transition-all">
                                        <div className="flex items-start space-x-4">
                                            <div className="flex-shrink-0">
                                                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center group-hover:bg-amber-200 transition-colors">
                                                    <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                                    </svg>
                                                </div>
                                            </div>
                                            <div className="flex-grow">
                                                <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">How to Improve Over Time</h3>
                                                <p className="text-sm text-gray-600 mt-2">Follow a structured improvement plan with weekly goals and progressive skill building.</p>
                                            </div>
                                        </div>
                                    </div>
                                </Link>

                                {/* Test Format and Workflow */}
                                <Link href="/learn/test-format-and-workflow" className="group">
                                    <div className="bg-white border rounded-lg p-6 hover:border-blue-500 hover:shadow-md transition-all">
                                        <div className="flex items-start space-x-4">
                                            <div className="flex-shrink-0">
                                                <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center group-hover:bg-violet-200 transition-colors">
                                                    <svg className="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                                    </svg>
                                                </div>
                                            </div>
                                            <div className="flex-grow">
                                                <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">Test Format & Workflow</h3>
                                                <p className="text-sm text-gray-600 mt-2">Understand test structure, navigation tips, and efficient workflow strategies.</p>
                                            </div>
                                        </div>
                                    </div>
                                </Link>

                                {/* Using Learning Aids */}
                                <Link href="/learn/using-learning-aids-effectively" className="group">
                                    <div className="bg-white border rounded-lg p-6 hover:border-blue-500 hover:shadow-md transition-all">
                                        <div className="flex items-start space-x-4">
                                            <div className="flex-shrink-0">
                                                <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center group-hover:bg-pink-200 transition-colors">
                                                    <svg className="w-6 h-6 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                                                    </svg>
                                                </div>
                                            </div>
                                            <div className="flex-grow">
                                                <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">Using Learning Aids Effectively</h3>
                                                <p className="text-sm text-gray-600 mt-2">Maximize learning with arithmetic and spatial aids, then transition to independent practice.</p>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </div>
                        </section>

                        <section className="mt-12 bg-gray-50 p-8 rounded-lg">
                            <h2 className="text-2xl font-semibold mb-4">Why Practice with Our Free Test Suite?</h2>
                            <ul className="space-y-4">
                                <li className="flex items-start">
                                    <svg className="w-6 h-6 text-green-500 mr-2 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    <p>Comprehensive general intelligence test preparation</p>
                                </li>
                                <li className="flex items-start">
                                    <svg className="w-6 h-6 text-green-500 mr-2 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    <p>Practice all aspects of the Thomas GIA test format</p>
                                </li>
                                <li className="flex items-start">
                                    <svg className="w-6 h-6 text-green-500 mr-2 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    <p>Free test pack with detailed performance tracking</p>
                                </li>
                                <li className="flex items-start">
                                    <svg className="w-6 h-6 text-green-500 mr-2 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    <p>Unique visual learning aids to help you improve your performance</p>
                                </li>
                            </ul>
                        </section>
                    </div>
                    
                    
                    {/* Sidebar ad - visible on large screens */}
                    <div className="lg:w-72">
                        <div className="hidden lg:block">
                            <AdPlacement position="sidebar" className="sticky top-4" />
                        </div>
                        <div className="mt-6 lg:hidden">
                            <AdPlacement position="content-bottom" />
                        </div>
                    </div>
                </div>

                {configType && (
                    <TestConfigModal
                        type={configType}
                        isOpen={true}
                        onClose={() => setConfigType(null)}
                    />
                )}
                {isMockOpen && (
                    <MockTestConfigModal
                        isOpen={true}
                        onClose={() => setIsMockOpen(false)}
                        onStart={(minutes) => {
                            setIsMockOpen(false);
                            router.push(`/mock?minutes=${minutes}`);
                        }}
                    />
                )}
            </main>
        </>
    );
} 